/**
 * Hanging protocols — auto-layout series into viewports based on rules.
 *
 * A protocol matches by modality/body-part/min-series. Highest priority wins.
 * The protocol config is a JSON array of viewport assignments:
 *   [ { vp: 0, seriesMatch: { modality: 'CT', desc: 'Axial' } }, ... ]
 * where seriesMatch can include: modality (exact), desc (case-insensitive substring),
 * index (by order after sort), or seriesNumber (exact).
 */
import { apiCall } from '../api/client';
import { StudyData, RENDERING_ENGINE_ID } from '../cornerstone/init';
import { getRenderingEngine } from '@cornerstonejs/core';
import { LayoutType, useViewerStore } from '../stores/viewerStore';
import { ViewportGridHandle } from '../components/ViewportGrid';
import { findPreset } from './wlPresets';

export interface HangingProtocol {
  id: number;
  name: string;
  modality?: string;
  bodyPart?: string;
  minSeries?: number;
  priority: number;
  layout: LayoutType;
  config: ViewportAssignment[];
  isDefault: boolean;
}

export interface ViewportAssignment {
  vp: number;
  seriesMatch: {
    modality?: string;
    desc?: string;
    index?: number;
    seriesNumber?: string;
  };
  /** Optional display overrides applied after the series finishes loading. */
  display?: {
    /** Named preset from wlPresets.ts (e.g. "Lung"). Takes precedence over wlWindow/wlCenter. */
    wlPreset?: string;
    /** Explicit window width + center if no preset is specified. */
    wlWindow?: number;
    wlCenter?: number;
    /** Where to seek the stack to after load. Numeric = 0-based index. */
    initialSlice?: 'first' | 'middle' | 'last' | number;
    invert?: boolean;
  };
}

export async function listProtocols(): Promise<HangingProtocol[]> {
  const res = await apiCall<any>('protocols.list', {});
  if (res.s !== 'ok' || !res.data) return [];
  return res.data.map(parseProtocol);
}

export async function matchProtocol(
  studyId: number,
  modality: string,
  bodyPart: string | undefined,
  numSeries: number
): Promise<HangingProtocol | null> {
  // DICOM modality codes are uppercase (PS3.5 VR CS). Uppercase before
  // sending so backends with case-sensitive SQL collation still match
  // protocols saved with the canonical casing.
  const res = await apiCall<any>('protocols.match', {
    studyId,
    modality: (modality || '').toUpperCase(),
    bodyPart: bodyPart || '',
    numSeries,
  });
  if (res.s !== 'ok' || !res.data || res.data.length === 0) return null;
  return parseProtocol(res.data[0]);
}

export async function saveProtocol(p: Partial<HangingProtocol>): Promise<number | null> {
  const res = await apiCall<{ protocolId: number }>('protocols.save', {
    protocolId: p.id ?? 0,
    name: p.name ?? 'Unnamed',
    modality: p.modality ?? '',
    bodyPart: p.bodyPart ?? '',
    minSeries: p.minSeries ?? 0,
    priority: p.priority ?? 100,
    layout: p.layout ?? '1x1',
    config: JSON.stringify(p.config ?? []),
    isDefault: p.isDefault ? 1 : 0,
  });
  return res.s === 'ok' ? res.data?.[0]?.protocolId ?? null : null;
}

export async function deleteProtocol(id: number): Promise<boolean> {
  const res = await apiCall('protocols.delete', { protocolId: id });
  return res.s === 'ok';
}

function parseProtocol(row: any): HangingProtocol {
  let config: ViewportAssignment[] = [];
  try { config = JSON.parse(row.config || '[]'); } catch {}
  return {
    id: row.protocolId,
    name: row.name,
    modality: row.modality,
    bodyPart: row.bodyPart,
    minSeries: row.minSeries,
    priority: row.priority,
    layout: (row.layout || '1x1') as LayoutType,
    config,
    isDefault: !!row.isDefault,
  };
}

/**
 * Read the current live state of every viewport and convert it into a
 * ViewportAssignment[] that can be saved as a hanging protocol.
 *
 * Matcher strategy: prefer series number (DICOM-stable across studies of the
 * same protocol) with index as fallback. Display settings are captured from
 * the viewport's current VOI range, invert flag, and slice index.
 */
export function captureCurrentView(studyData: StudyData): ViewportAssignment[] {
  const engine = getRenderingEngine(RENDERING_ENGINE_ID);
  if (!engine) return [];

  const config: ViewportAssignment[] = [];

  for (const vp of engine.getViewports()) {
    const m = /^viewport-(\d+)$/.exec((vp as any).id || '');
    if (!m) continue;
    const vpIndex = Number(m[1]);

    const imageIds = (vp as any).getImageIds?.() || [];
    const firstId = imageIds[0];
    if (!firstId) continue;
    const sm = /seriesId=(\d+)/.exec(firstId);
    if (!sm) continue;
    const seriesId = Number(sm[1]);
    const seriesIdx = studyData.series.findIndex((s) => s.seriesId === seriesId);
    if (seriesIdx < 0) continue;
    const series = studyData.series[seriesIdx];

    // Prefer SeriesNumber — stable across studies with the same acquisition
    // protocol. Fall back to ordinal index when SN is missing.
    const seriesMatch = series.seriesNumber
      ? { seriesNumber: String(series.seriesNumber) }
      : { index: seriesIdx };

    const display: NonNullable<ViewportAssignment['display']> = {};
    try {
      const props = (vp as any).getProperties?.() || {};
      if (props.voiRange) {
        display.wlWindow = Math.round(props.voiRange.upper - props.voiRange.lower);
        display.wlCenter = Math.round((props.voiRange.upper + props.voiRange.lower) / 2);
      }
      if (props.invert) display.invert = true;
    } catch { /* viewport shape without properties (e.g. volume 3D) */ }

    try {
      const idx = (vp as any).getCurrentImageIdIndex?.();
      if (typeof idx === 'number' && idx > 0) display.initialSlice = idx;
    } catch { /* not a stack viewport */ }

    const assignment: ViewportAssignment = { vp: vpIndex, seriesMatch };
    if (Object.keys(display).length > 0) assignment.display = display;
    config.push(assignment);
  }

  return config.sort((a, b) => a.vp - b.vp);
}

/** Find the series that best matches the given criteria */
function findSeries(
  studyData: StudyData,
  match: ViewportAssignment['seriesMatch']
): number | null {
  const series = studyData.series;

  if (typeof match.index === 'number') {
    return series[match.index]?.seriesId ?? null;
  }
  if (match.seriesNumber) {
    const s = series.find((x) => x.seriesNumber === match.seriesNumber);
    if (s) return s.seriesId;
  }

  const candidates = series.filter((s) => {
    // Modality codes are ALWAYS case-insensitive in DICOM (PS3.5 §6.2 —
    // VR "CS" is uppercase, but user-entered HP records may have been
    // saved 'mg' by mistake). Case-folding both sides prevents silent
    // no-match when the casing differs.
    if (match.modality) {
      const seriesMod = (s.modality || '').toUpperCase();
      const wantMod = match.modality.toUpperCase();
      if (seriesMod !== wantMod) return false;
    }
    if (match.desc) {
      const needle = match.desc.toLowerCase();
      if (!(s.seriesDescription || '').toLowerCase().includes(needle)) return false;
    }
    return true;
  });
  return candidates[0]?.seriesId ?? null;
}

/**
 * Auto-apply the best-matching protocol on first study open. Delegates
 * to applyMatchedProtocol once matchProtocol has returned. Used by the
 * viewer's addStudy flow; the ProtocolsDialog "Apply" button calls
 * applyMatchedProtocol directly with a user-picked protocol.
 */
export async function applyHangingProtocol(
  studyData: StudyData,
  grid: ViewportGridHandle | null
): Promise<boolean> {
  if (!grid || !studyData) return false;

  const protocol = await matchProtocol(
    studyData.studyId,
    studyData.modality || '',
    undefined,
    studyData.series?.length || 0
  );
  if (!protocol) return false;
  return applyMatchedProtocol(protocol, studyData, grid);
}

/**
 * Apply a specific protocol (already looked up) to the study — set the
 * layout, then loadSeries into each viewport per the config. Used by
 * both the auto-apply-on-open path and the ProtocolsDialog "Apply"
 * button.
 */
export async function applyMatchedProtocol(
  protocol: HangingProtocol,
  studyData: StudyData,
  grid: ViewportGridHandle | null
): Promise<boolean> {
  if (!grid || !studyData || !protocol) return false;

  // Frontend safety net: never apply a modality-tagged protocol cross-modality.
  // A user-built MG hanging protocol rearranging four mammo views is actively
  // misleading on a CT abdomen. Universal (modality === '') protocols still
  // pass through. (#12)
  if (protocol.modality
    && protocol.modality.toUpperCase() !== (studyData.modality || '').toUpperCase()) {
    console.warn(
      '[HP] cross-modality safety-net reject:', protocol.modality, 'vs', studyData.modality
    );
    return false;
  }

  useViewerStore.getState().setLayout(protocol.layout);

  // Wait for layout to apply (viewports need to exist)
  await new Promise((r) => setTimeout(r, 100));

  for (const asgn of protocol.config) {
    const seriesId = findSeries(studyData, asgn.seriesMatch);
    if (seriesId != null) {
      grid.loadSeries(seriesId, asgn.vp);
      if (asgn.display) applyDisplayAfterLoad(asgn.vp, asgn.display);
    }
  }
  return true;
}

/** Apply the protocol's display overrides once the series has finished loading.
 *  Image load is async and Cornerstone doesn't always fire a ready event we can
 *  reliably await, so we poll briefly for the viewport to have image data. */
function applyDisplayAfterLoad(
  vpIndex: number,
  display: NonNullable<ViewportAssignment['display']>,
  attempt = 0
): void {
  const engine = getRenderingEngine(RENDERING_ENGINE_ID);
  const vp: any = engine?.getViewport(`viewport-${vpIndex}`);
  const ready = vp?.getImageIds?.()?.length > 0;
  if (!ready) {
    if (attempt > 60) return;
    setTimeout(() => applyDisplayAfterLoad(vpIndex, display, attempt + 1), 50);
    return;
  }

  try {
    // 1. W/L — preset takes precedence over explicit WW/WC
    let ww: number | undefined;
    let wc: number | undefined;
    if (display.wlPreset) {
      const p = findPreset(display.wlPreset);
      if (p) { ww = p.ww; wc = p.wc; }
    }
    if (ww == null && display.wlWindow != null) ww = display.wlWindow;
    if (wc == null && display.wlCenter != null) wc = display.wlCenter;
    if (ww != null && wc != null) {
      vp.setProperties({ voiRange: { lower: wc - ww / 2, upper: wc + ww / 2 } });
    }

    // 2. Invert
    if (display.invert != null) {
      const cur = vp.getProperties().invert;
      if (cur !== display.invert) vp.setProperties({ invert: display.invert });
    }

    // 3. Initial slice
    if (display.initialSlice != null && vp.setImageIdIndex && vp.getImageIds) {
      const total = vp.getImageIds().length;
      let idx = 0;
      if (display.initialSlice === 'first') idx = 0;
      else if (display.initialSlice === 'last') idx = total - 1;
      else if (display.initialSlice === 'middle') idx = Math.floor(total / 2);
      else if (typeof display.initialSlice === 'number') idx = Math.max(0, Math.min(total - 1, display.initialSlice));
      vp.setImageIdIndex(idx);
    }

    vp.render();
  } catch {
    // Viewport shape doesn't match — e.g. MPR volume viewport — silently skip.
  }
}
